/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { ArchiveService } from '@alfresco/aca-shared';
import { AppStore, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { TranslationService } from '@alfresco/adf-core';
import { Node } from '@alfresco/js-api';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogModule,
    MatButtonModule,
  ],
  templateUrl: './insert-into-subject.dialog.html',
  styleUrls: ['./insert-into-subject.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'aca-insert-into-subject-dialog' }
})
export class InsertIntoSubjectDialogComponent implements OnInit {
  node: Node;

  registries: any[] = [];
  selectedSubject: any | undefined = undefined;
  attemptedToFindSelectedSubject: boolean = false;

  archiveForm = new FormGroup({
    selectedRegistry: new FormControl(undefined, Validators.required),
    subjectIndex: new FormControl(undefined, [Validators.min(1), this.validateIndex()]),
    sequentialIndex: new FormControl(undefined, [Validators.min(1), this.validateSeqIndex()]),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private store: Store<AppStore>,
    private archiveService: ArchiveService,
    private translate: TranslationService,
    private dialogRef: MatDialogRef<InsertIntoSubjectDialogComponent>
  ) {
    this.node = data.node.entry;

    this.selectedRegistry.valueChanges.subscribe((_) => {
      this.onSelectedRegistryChanged();
    });

    this.subjectIndex.valueChanges.subscribe((_) => {
      this.onSubjectIndexChanged();
    });

    this.sequentialIndex.valueChanges.subscribe((_) => {
      this.onSequentialIndexChanged();
    });
  }

  get selectedRegistry() {
    return this.archiveForm.get('selectedRegistry');
  }

  get subjectIndex() {
    return this.archiveForm.get('subjectIndex');
  }

  get sequentialIndex() {
    return this.archiveForm.get('sequentialIndex');
  }

  get selectedRegistryCounter(): number {
    const selectedRegistry = this.registries.find((registry) => registry['nodeRef'] === this.selectedRegistry.value);

    return selectedRegistry?.['counter'] ?? 0;
  }

  async ngOnInit() {
    this.registries = await this.archiveService.getRegistryBooks();
  }

  printSelectedSubject(): string {
    if (!this.selectedSubject)
      return '';
    return `${this.selectedSubject['archiveSign']}-${this.selectedSubject['index']}/${this.selectedSubject['seqIndex']} ${this.selectedSubject['subject']}`;
  }

  async insertIntoSubject() {
    if (this.archiveForm.invalid) return;

    try {
      await this.archiveService.insertDocumentIntoSubject(
        `workspace://SpacesStore/${this.node.id}`,
        this.selectedRegistry.value,
        this.subjectIndex.value.toString(),
        this.sequentialIndex.value.toString(),
      );

      this.store.dispatch(new SnackbarInfoAction(this.translate.instant('APP.ARCHIVE.INSERT_INTO_SUBJECT.SUCCESS.INSERT_INTO_SUBJECT')));
      this.dialogRef.close();
    } catch (e) {
      this.store.dispatch(new SnackbarErrorAction(this.translate.instant('APP.ARCHIVE.INSERT_INTO_SUBJECT.ERROR.INSERT_INTO_SUBJECT')));
    }
  }

  private onSelectedRegistryChanged() {
    this.subjectIndex.setValue(undefined);
    this.sequentialIndex.setValue(undefined);
    this.selectedSubject = undefined;
    this.attemptedToFindSelectedSubject = false;
  }

  private onSubjectIndexChanged() {
    this.sequentialIndex.setValue(undefined);
    this.selectedSubject = undefined;
    this.attemptedToFindSelectedSubject = false;
  }

  private onSequentialIndexChanged() {
    this.selectedSubject = undefined;
    this.attemptedToFindSelectedSubject = false;
    if (this.subjectIndex.value && this.sequentialIndex.value) {
      this.archiveService.getSubjectAtIndex(this.selectedRegistry.value, this.subjectIndex.value)
        .then((value: object[]) => {
          this.selectedSubject = value.find(el => el['seqIndex'] === this.sequentialIndex.value.toString());
          this.attemptedToFindSelectedSubject = true;
          this.sequentialIndex.updateValueAndValidity({
            emitEvent: false,
          });
        })
        .catch(_ => {
          this.store.dispatch(new SnackbarErrorAction(this.translate.instant('CORE.MESSAGES.ERRORS.GENERIC')));
        });
    }
  }

  private validateIndex(): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      const selectedRegistry = this.registries.find((registry) => registry['nodeRef'] === this.selectedRegistry.value);

      if (!selectedRegistry) return null;
      
      if (selectedRegistry['invActs'].split(',').includes(control.value?.toString()) || control.value > parseInt(this.selectedRegistryCounter.toString()))
        return { invalidIndex: { value: control.value } };

      return null;
    };
  }

  private validateSeqIndex(): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      if (this.attemptedToFindSelectedSubject && !this.selectedSubject)
        return { invalidIndex: { value: control.value } };
      return null;
    };
  }
}
